<script setup>
import { ref, onMounted, getCurrentInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { usePageLoadingStore } from "../../../../stores/pageLoading";
import SNBAdmin from "../../../../layouts/nav/SNBAdmin.vue";
import adminUserService from "../../services/user/adminUserService";
import adminPartnerService from "../../services/partner/adminPartnerService";
import KSASecondaryButton from "../../../../components/ui-components/button/KSASecondaryButton.vue";
import KSACertificateTemplatePreview from "../../../../components/ui-components/KSACertificateTemplatePreview.vue";
import { useAlertStore } from "../../../../stores/alertStore";

const { $alert } = useAlertStore();
const route = useRoute();
const router = useRouter();
const loadingStore = usePageLoadingStore();
const { $pageListBoardNo, $toast, $yyyymmdd, $yyyymmddHHmmss, $phoneNum, $number } = getCurrentInstance().appContext.config.globalProperties;

const partnerId = ref(0);

onMounted(() => {
    const { id } = route.params;
    if (!id) {
        router.push({ name: "PartnerList" });
    } else {
        partnerId.value = id;
        retrievePartner(id);
    }
});

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(0);
const totalCount = ref(0);

const tabIndex = ref(0);

const partnerDetail = ref({
    certificateTemplate: {},
});  // tab1. 담당기관 정보
const partnerAdminList = ref([]);  // tab2. 관리자

// S : tab1. 담당기관 정보
// 담당기관 상세 조회
const retrievePartner = (partnerId) => {
    loadingStore.loading = true;
    adminPartnerService.retrievePartner(partnerId)
        .then( async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                partnerDetail.value = res.data.result;
            }
        }).catch((res) => {
            loadingStore.loading = false;
            $toast("error", res);
    });
};

// 담당기관 수정
const editPartner = () => {
    router.push({ name: "PartnerUpdate", params: { id: partnerDetail.value.partnerId } });
};

// 담당기관 삭제 - 버튼 주석 처리
const deletePartner = () => {
    loadingStore.loading = true;
    adminPartnerService.deletePartner(partnerDetail.value)
        .then(async (res) => {
            loadingStore.loading = false;
            if (res.data.code.toLowerCase() === "success") {
                $alert(["담당기관이 삭제되었습니다."]);
                goPartnerList();
            }
        }).catch((res) => {
            loadingStore.loading = false;
            $toast("error", res);
    });
};
// E : tab1. 담당기관 정보

// S : tab2. 관리자
// 관리자 리스트 조회
const retrievePartnerAdminList = () => {
    let paramObj = {
        partnerId: partnerId.value,
        pageNo: currentPage.value,
        size: pageSize.value,
    }

    loadingStore.loading = true;
    adminUserService.retrievePartnerAdminPagingByPartnerId(paramObj)
        .then( async (res) => {
            loadingStore.loading = false;
            // console.log(res);
            if(res.data.code.toLowerCase() === 'success') {
                const result = res.data.result;
                totalPages.value = result.totalPages;
                totalCount.value = result.totalElements;
                partnerAdminList.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
            }
        }).catch( (error) =>  {
            // console.log(error);
            loadingStore.loading = false;
    });
}
// E : tab2. 관리자

// '목록' 버튼(담당기관 목록으로 이동)
const goPartnerList = () => {
    router.push({ name: "PartnerList" });
};

// 페이지 선택
const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
};

// tabIndex 변경
const onChangeTab = async ({ value }) => {
    tabIndex.value = value;
    currentPage.value = 1;
    retrieveList();
}

const retrieveList = () => {
    if(tabIndex.value === 0){
        retrievePartner(partnerId.value)
    } else if (tabIndex.value === 1){
        retrievePartnerAdminList();
    }
}
</script>

<template>
    <div class="content">
        <div class="sub-title">
            <h3>{{ partnerDetail.partnerName }}</h3>
        </div>
        <div class="list-top space-between">
            <KSASecondaryButton class="gray_button" text="목록" @click="goPartnerList" />
            <div>
                <KSASecondaryButton class="gray_button ml-xs" text="수정" @click="editPartner" />
                <!-- <KSASecondaryButton class="red_button ml-xs" text="삭제" @click="deletePartner"/> -->
            </div>
        </div>
        <KSATab style="grid-template-columns: repeat(2, 1fr);" class="mb-lg"
                :options="[
                    {label:'담당기관 정보', value:0},
                    {label:'관리자 현황', value:1},
                ]"
                @change="onChangeTab"
        />

                <!-- s: 1. 담당기관 정보 -->
                <div v-show ="tabIndex === 0">
                    <div class="table-form">
                        <table class="vertical">
                            <colgroup>
                                <col width="200px" />
                                <col />
                            </colgroup>
                            <tr>
                                <th>담당기관명</th>
                                <td>
                                    <span>{{ partnerDetail.partnerName }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>담당기간</th>
                                <td>
                                    <span>
                                        {{ $yyyymmdd(partnerDetail.partnerStartYmd) }} ~ {{ $yyyymmdd(partnerDetail.partnerEndYmd) }}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>대표자명</th>
                                <td>
                                    <span>{{ partnerDetail.ceoName }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>담당기관 연락처</th>
                                <td>
                                    <span>{{ partnerDetail.partnerPhoneNum }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>담당기관 이메일</th>
                                <td>
                                  <span>{{ partnerDetail.partnerEmail }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>담당자명</th>
                                <td>
                                    <span>{{ partnerDetail.chargeName }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>담당자 연락처</th>
                                <td>
                                    <span>{{ partnerDetail.chargePhoneNum }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>담당자 이메일</th>
                                <td>
                                    <span>{{ partnerDetail.chargeEmail }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>발송 연락처</th>
                                <td>
                                <span>{{ partnerDetail.partnerSenderNum }}</span>
                                </td>
                            </tr>
                            <tr>
                                <th>발송 이메일</th>
                                <td>
                                <span>{{ partnerDetail.partnerSenderEmail }}</span>
                                </td>
                            </tr>
                            <tr>
                               <th>수료증 양식</th>
                               <td>
                                   <KSACertificateTemplatePreview 
                                       :headSrc="partnerDetail.certificateTemplate?.frontImageDir"
                                       :tailSrc="partnerDetail.certificateTemplate?.backImageDir"
                                       :title="partnerDetail.certificateTemplate?.certificateName"
                                       :frontFixedText="partnerDetail.certificateTemplate?.frontFixedText"
                                   />
                               </td>
                           </tr>
                        </table>
                    </div>
                </div>
                <!-- e: 1. 담당기관 정보 -->

        <!-- s: 2. 관리자 -->
        <div v-show = "tabIndex === 1">
            <div class="list-top">
                <p>총 <span>{{ $number(partnerAdminList.length)}}</span>명</p>
            </div>
            <div class="table-form">
                <table class="horizontal">
                    <tr>
                        <th>NO{{ partnerAdminList.length }}</th>
                        <th>이름</th>
                        <th>아이디</th>
                        <th>핸드폰 번호</th>
                        <th>이메일</th>
                        <th>최근 접속일</th>
                        <th>등록일</th>
                    </tr>
                    <tr v-for="list in partnerAdminList">
                        <td>{{ list.boardNo }}</td>
                        <td class="link"><RouterLink :to="{name:'ManagerDetail', params:{id: list.userId}}">{{ list.userName }}</RouterLink></td>
                        <td>{{ list.userLoginId }}</td>
                        <td>{{ $phoneNum(list.phoneNum) }}</td>
                        <td>{{ list.email }}</td>
                        <td>{{ $yyyymmddHHmmss(list.lastLoginDate) }}</td>
                        <td>{{ $yyyymmddHHmmss(list.createDate) }}</td>
                    </tr>
                </table>
                <div class="result-none" v-if="partnerAdminList.length === 0">
                    등록된 담당기관 관리자가 없습니다.
                </div>
            </div>
            <Paginate
                v-if="partnerAdminList.length > 0"
                v-model="currentPage"
                :page-count="totalPages"
                :click-handler="onSelectPage"
            />
        </div>
        <!-- e: 2. 관리자 -->
    </div>
</template>

<style lang="scss" scoped>
@import "../../../../assets/scss/theme";

.textarea-box {
    height: 100px;
}
</style>